import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { AccessModel, AppContentType } from '@jwp/ott-common/types/config';
import { isLocked } from '@jwp/ott-common/src/utils/entitlements';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { PersonalShelf } from '@jwp/ott-common/src/constants';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import useBreakpoint, { Breakpoint, type Breakpoints } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import type { PosterAspectRatio } from '@jwp/ott-common/src/utils/collection';

import Card from '../../components/Card/Card';
import { TileSlider } from '../../components/TileDock/TileDock';
import Icon from '../../components/Icon/Icon';
import createInjectableComponent from '../../modules/createInjectableComponent';

import styles from './RelatedVideoShelf.module.scss';

export type Direction = 'left' | 'right';
export type CycleMode = 'stop' | 'restart' | 'endless';
export type RenderTile<T> = (params: {
  item: T;
  itemIndex: number;
  isVisible: boolean;
  index: number;
  slide: (direction: Direction) => void;
}) => React.ReactElement;

export type RenderControl = (props: ControlProps) => React.ReactElement;
export type RenderPagination = (props: PaginationProps) => React.ReactElement;
export type ControlProps = {
  onClick: () => void;
  disabled: boolean;
};
export type PaginationProps = {
  index: number;
  itemIndex: number;
  total: number;
  page: number;
  pages: number;
  slide: (direction: Direction) => void;
  slideToIndex: (index: number) => void;
  slideToPage: (page: number) => void;
};
export const tileBreakpoints: Breakpoints = {
  [Breakpoint.xs]: 1,
  [Breakpoint.sm]: 2,
  [Breakpoint.md]: 3,
  [Breakpoint.lg]: 4,
  [Breakpoint.xl]: 5,
};

export const featuredTileBreakpoints: Breakpoints = {
  [Breakpoint.xs]: 1,
  [Breakpoint.sm]: 1,
  [Breakpoint.md]: 1,
  [Breakpoint.lg]: 1,
  [Breakpoint.xl]: 1,
};

export const ShelfIdentifier = Symbol(`SHELF`);

export type ShelfProps = {
  playlist: Playlist;
  type: AppContentType;
  onCardHover?: (playlistItem: PlaylistItem) => void;
  watchHistory?: { [key: string]: number };
  enableTitle?: boolean;
  enableCardTitles?: boolean;
  featured?: boolean;
  loading?: boolean;
  currentCardItem?: PlaylistItem;
  currentCardLabel?: string;
  error?: unknown;
  title?: string;
  accessModel: AccessModel;
  isLoggedIn: boolean;
  hasSubscription: boolean;
  posterAspect?: PosterAspectRatio;
  visibleTilesDelta?: number;
  customShelfValues?: any;
  getUrl: (item: PlaylistItem) => string;
};

const RelatedVideoShelf = ({
  playlist,
  type,
  onCardHover,
  title,
  watchHistory,
  featured = false,
  loading = false,
  currentCardItem,
  currentCardLabel,
  error = null,
  accessModel,
  isLoggedIn,
  hasSubscription,
  posterAspect,
  visibleTilesDelta = 0,
  customShelfValues,
  getUrl,
}: ShelfProps) => {
  const breakpoint: Breakpoint = useBreakpoint();

  const { t } = useTranslation('common');
  const tilesToShow: number = (featured ? featuredTileBreakpoints[breakpoint] : tileBreakpoints[breakpoint]) + visibleTilesDelta;

  const isShowTopTenIcon = useCallback(() => {
    return playlist?.railType == 'top-rated';
  }, [playlist?.railType]);

  const isOverlayTestVisible = useCallback(() => {
    const customShelfValue = customShelfValues ?? {};
    const customShowTitle = Object.entries(customShelfValue).find(([key]) => key.replace(/_/g, '').toLowerCase() === 'showtitle')?.[1];
    if (typeof customShowTitle === 'string' && customShowTitle !== '') {
      return customShowTitle.toLowerCase() !== 'false';
    } else {
      const showTitleEntry = Object.entries(playlist).find(([key]) => key.replace(/_/g, '').toLowerCase() === 'showtitle');

      const showTitleValue = showTitleEntry?.[1];

      if (typeof showTitleValue === 'string' && showTitleValue !== '') {
        return showTitleValue.toLowerCase() !== 'false';
      } else {
        return true;
      }
    }
  }, [playlist]);

  const renderTile = useCallback(
    ({
      item,
      isVisible,
      itemIndex,
      isShowTopTenIcon,
      isOverlayTestVisible,
    }: {
      item: PlaylistItem;
      itemIndex: number;
      index: number;
      isVisible: boolean;
      topTenIndex?: number;
      isShowTopTenIcon: boolean;
      isOverlayTestVisible: boolean;
    }) => {
      const { mediaid: id, title } = item;
      const url = mediaURL({ id, title, playlistId: playlist.feedid, play: type === PersonalShelf.ContinueWatching });

      return (
        <Card
          key={item.mediaid}
          progress={watchHistory ? watchHistory[item.mediaid] : undefined}
          onHover={typeof onCardHover === 'function' ? () => onCardHover(item) : undefined}
          featured={featured}
          disabled={!isVisible}
          loading={loading}
          isCurrent={currentCardItem && currentCardItem.mediaid === item.mediaid}
          currentLabel={currentCardLabel}
          isLocked={isLocked(accessModel, isLoggedIn, hasSubscription, item)}
          posterAspect={posterAspect}
          item={item}
          url={getUrl(item)}
          isShowTopTenIcon={isShowTopTenIcon}
          topTenIndex={itemIndex}
          railType={typeof playlist?.railType === 'string' ? playlist?.railType : ''}
          isOverlayTestVisible={isOverlayTestVisible}
        />
      );
    },
    [watchHistory, onCardHover, featured, loading, accessModel, isLoggedIn, hasSubscription, posterAspect, playlist.feedid, type],
  );

  const renderRightControl: RenderControl = useCallback(
    ({ onClick, disabled }) => (
      <button style={{ display: disabled ? 'none' : 'block' }} className={styles.chevron} aria-label={t('slide_next')} onClick={onClick}>
        <Icon icon={ChevronRight} />
      </button>
    ),
    [t],
  );

  const renderLeftControl: RenderControl = useCallback(
    ({ onClick, disabled }) => (
      <button style={{ display: disabled ? 'none' : 'block' }} className={styles.chevron} aria-label={t('slide_previous')} onClick={onClick}>
        <Icon icon={ChevronLeft} />
      </button>
    ),
    [t],
  );

  if (error || !playlist?.playlist) return <h2 className={styles.error}>Could not load items</h2>;

  return (
    <section className={classNames(styles.shelfContainer)}>
      <div className={classNames(styles.shelf)}>
        <TileSlider<PlaylistItem>
          className={styles.slider}
          items={playlist.playlist}
          title={true}
          tilesToShow={tilesToShow}
          cycleMode={featured ? 'restart' : 'stop'}
          showControls={!loading}
          spacing={16}
          renderLeftControl={renderLeftControl}
          renderRightControl={renderRightControl}
          renderPagination={null}
          renderTile={renderTile}
          isShowTopTenIconFlag={isShowTopTenIcon()}
          isrelatedShelfSLider={true}
          isOverlayTestVisible={isOverlayTestVisible()}
        />
      </div>
    </section>
  );
};

export default createInjectableComponent(ShelfIdentifier, RelatedVideoShelf);
